/**
 * Coach-facing copy for a focus-area region in the checkpoint list (PR-4).
 *
 * A focus area is in one of two states and the coach has to be able to tell
 * which, at a glance, in words:
 *
 *   RESOLVED    its body points were matched to one side of the body, so it
 *               carries the weighting the coach chose into the score.
 *   UNRESOLVED  it is on the template and counts for nothing yet, because we
 *               could not tell which limb the coach meant.
 *
 * Every string here answers WHAT / WHY / HOW for a non-technical reader
 * (docs/COACH_FACING_COPY_STANDARD.md §1) and names body parts in the coach's
 * own language via `landmarkDisplay.ts` — never a raw identifier, never a
 * count of "landmarks" (§2 forbids both). Where the backend already owns a
 * sentence — the notice's what / why / how — this module composes NOTHING and
 * the component renders the backend's words verbatim (the materialise
 * contract's note 2, `types/api.ts`).
 *
 * These are pure functions so the copy can be read, reviewed and pinned
 * without rendering a canvas.
 */

import { landmarkDisplayNames } from './landmarkDisplay';

/** Which physical side a region resolved to. */
export type ResolvedSide = 'left' | 'right';

/**
 * "a", "a and b", "a, b and c" — the way a coach says a list out loud.
 * Returns `null` for an empty list so the caller omits the whole phrase
 * rather than rendering a dangling label.
 */
export function joinInPlainEnglish(items: readonly string[]): string | null {
  if (items.length === 0) return null;
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The badge on a region that counts in the score. Plain words, no jargon. */
export const COUNTING_IN_SCORES_BADGE = 'Counting in scores';

/**
 * The one-line "not yet" sentence, unchanged from PR-3 for the case where the
 * backend sent no notice: at that point nothing has been decided yet and the
 * promise it makes ("when you finish the template") is the true one.
 *
 * When a notice IS present the decision has been made and failed closed, so
 * the component drops this tail and renders the notice's own what / why / how
 * instead — keeping a promise we are not going to keep would be worse than
 * saying nothing.
 */
export const NOT_COUNTING_YET_PENDING =
  "Not counting in scores yet — we'll work out which side of the body this is " +
  'on when you finish the template.';

/** The short lead-in used when a notice supplies the rest of the message. */
export const NOT_COUNTING_YET_SHORT = 'Not counting in scores yet.';

/**
 * What a resolved region is watching, in the coach's words.
 *
 * `landmark_names` is anatomical by contract (plan §C.3a guard 1), so
 * `landmarkDisplayNames` turns `["right_knee","right_ankle"]` into
 * `"right knee"` / `"right ankle"` and silently drops anything outside the
 * known vocabulary rather than printing the raw token.
 *
 * Returns `null` when there is nothing truthful to say — the caller then shows
 * the badge alone instead of an empty label.
 */
export function regionWatchingSentence(
  landmarkNames: readonly string[] | undefined,
  side: ResolvedSide | undefined,
): string | null {
  const parts = joinInPlainEnglish(landmarkDisplayNames(landmarkNames ?? []));
  if (parts && side) return `We're watching the ${parts}, on the ${side} side.`;
  if (parts) return `We're watching the ${parts}.`;
  if (side) return `Matched to the ${side} side of the body.`;
  return null;
}

/**
 * Whether a declared lead side is going to resolve this template's focus
 * areas — i.e. whether the studio may promise the coach that it will.
 *
 * This mirrors the fail-closed table in `REFACTOR_PLAN.md` §C.3a, which the
 * backend resolver implements. Only ONE of its four rows resolves:
 *
 *   asymmetric + a declared side  → resolved
 *   asymmetric, no declared side  → fails closed (`side_not_declared`)
 *   symmetric                     → fails closed (`symmetric_region_deferred`)
 *   rotational                    → fails closed (`rotational_region_deferred`)
 *   no declared symmetry          → fails closed (the common case)
 *
 * Rotational is the row worth stating out loud, because a rotational template
 * DOES have a lead side and the studio does collect it: the body turns right
 * through the movement, so the side a focus area would be pinned to changes
 * during it, and pinning one would be wrong rather than merely unknown.
 *
 * Returns `undefined` for every non-resolving row, so the caller shows no line
 * rather than a promise that will not be kept.
 */
export function declaredSideForFocusAreas(
  exerciseSymmetry: string | null | undefined,
  templateLeadSide: string | null | undefined,
): ResolvedSide | undefined {
  if (exerciseSymmetry !== 'asymmetric') return undefined;
  if (templateLeadSide !== 'left' && templateLeadSide !== 'right') return undefined;
  return templateLeadSide;
}

/**
 * The line the suggestions / checkpoint panel shows once the coach has told us
 * which side leads: what will happen to their focus areas, and when.
 *
 * Only the WHAT is needed here — nothing is wrong and there is nothing to fix,
 * so a why and a how would be noise (the standard's own "Distance looks right."
 * precedent, §3).
 */
export function focusAreaSideLine(side: ResolvedSide): string {
  return `Focus areas will be matched to the ${side} side when you save.`;
}

// ---------------------------------------------------------------------------
// The "Place on the video" action (owner decision D5)
// ---------------------------------------------------------------------------

/** The action's label. One click, said as the coach would say it. */
export const PLACE_ON_VIDEO_LABEL = 'Place on the video';

/** While the request is in flight. */
export const PLACE_ON_VIDEO_BUSY_LABEL = 'Placing…';

/**
 * Why the action cannot run. Each of these is a complete what / why / how, so
 * the coach never has to guess what a greyed-out button wants from them.
 *
 * `unresolved` deliberately does NOT repeat the how — the notice above it on
 * the same card already carries the backend's instruction (for
 * `side_not_declared`, that instruction points at the lead-side control), and
 * saying it twice in two wordings is audit item FE-12.
 */
export type PlacementBlockedReason =
  | 'unresolved'
  | 'template_not_saved'
  | 'no_box'
  | 'request_failed';

export const PLACEMENT_BLOCKED_SENTENCES: Record<PlacementBlockedReason, string> = {
  unresolved:
    "You can drop this onto the video once we know which side of the body it's on.",
  template_not_saved:
    'Save the template first, then you can drop this onto the video in one click.',
  no_box:
    "We couldn't work out where this sits in your video, because the body parts " +
    "it watches weren't clear enough in the frame. Draw the box yourself with " +
    'the rectangle tool.',
  request_failed:
    "We couldn't fetch a position just now. Try again, or draw the box yourself " +
    'with the rectangle tool.',
};

/**
 * The hint shown on the selected checkpoint so the coach knows the box on the
 * video can be moved — the behaviour audit item FE-9 says nothing announced.
 */
export const SELECTED_ON_CANVAS_HINT =
  'Selected — drag it on the video to move it, or drag a corner to resize it.';
